
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import Swiper, { SwiperOptions } from "swiper";
import { BuffetCategory, BuffetEpgItem } from "@/services/apiService/api.types";
import { BuffetCategories } from "@/helpers/constants";
import BuffetEpgCard from "@/components/buffet/BuffetEpgCard.vue";
import { sortedBuffetCategories } from "@/services/apiStorageInterface";
import SpatialNavigation from "spatial-navigation-js";
import { keyCodeFinder } from "@/services/keyCodeService/keyCodeService";
import { KeySet } from "@/services/keyCodeService/keyCode.types";
import { SpatialNavigationEvent } from "@/@types/spatial-navigation-js/types";
import { Getter, Mutation } from "vuex-class";
import { Namespace } from "@/store/types";
import {
  BuffetGetter,
  BuffetMutation,
  PlayedEpgItemCoordinates
} from "@/components/buffet/types";
import { ChannelsPageGetter } from "@/components/channelsPage/types";
@Component({
  components: { BuffetEpgCard }
})
export default class BuffetHomePage extends Vue {
  @Prop() readonly buffetCategories: Record<BuffetCategories, BuffetCategory> =
    sortedBuffetCategories;

  @Mutation(BuffetMutation.setUpdatedBuffetItems, {
    namespace: Namespace.Buffet
  })
  setUpdatedBuffetItems!: (value: boolean) => void;

  @Mutation(BuffetMutation.setPlayedEpgItemPos, { namespace: Namespace.Buffet })
  setPlayedEpgItemPos!: (slidePos?: PlayedEpgItemCoordinates) => void;

  @Mutation(BuffetMutation.setPlayedEpgItemCat, { namespace: Namespace.Buffet })
  setPlayedEpgItemCat!: (category?: BuffetCategories) => void;

  @Mutation(BuffetMutation.setPlayedEpgItemId, { namespace: Namespace.Buffet })
  setPlayedEpgItemId!: (epgId?: number) => void;

  @Mutation(BuffetMutation.setPlayedEpgItemDate, {
    namespace: Namespace.Buffet
  })
  setPlayedEpgItemDate!: (date?: string) => void;

  @Getter(ChannelsPageGetter.GetCardControls, {
    namespace: Namespace.ChannelsPage
  })
  getCardControlsStatus!: boolean;

  @Getter(BuffetGetter.getUpdatedBuffetItems, {
    namespace: Namespace.Buffet
  })
  getUpdatedBuffetItems!: boolean;

  @Getter(BuffetGetter.getPlayedEpgItemPos, {
    namespace: Namespace.Buffet
  })
  getPlayedEpgItemPos!: PlayedEpgItemCoordinates | undefined;

  categorySwipers: Swiper[] = [];
  activeSwiperIndex = 0; // determines active/focused swiper instance
  swiperActiveSlideIndexes: number[] = []; //indexes of active elements for each swiper - allows proper focusing when listing through swiper

  swiperOptions: SwiperOptions = {
    keyboard: {
      enabled: false
    },
    slidesPerView: 5,
    spaceBetween: 20,
    breakpoints: {
      // when window width is <= 768px
      768: {
        slidesPerView: 3,
        spaceBetween: 10
      },
      1400: {
        slidesPerView: 5,
        spaceBetween: 20
      }
    }
  };

  smallSwiper = false; // indicates swiper with three

  created(): void {
    window.addEventListener("resize", () => {
      this.checkWindowWidth();
    });
    // setting initial status
    this.checkWindowWidth();
  }
  mounted(): void {
    this.initializeSwipers();
  }

  initializeSwipers(): void {
    //create all required swiper instances
    this.$nextTick(() => {
      //initializing category swipers
      for (let i = 0; i < this.filteredBuffetCategories.length; i++) {
        const categorySwiperInstance = new Swiper(
          "#" +
            this.filteredBuffetCategories[i].name
              .replace(/\s/g, "")
              .replace(/&/g, "and"),
          this.swiperOptions
        );
        this.categorySwipers.push(categorySwiperInstance);
      }
      this.swiperActiveSlideIndexes = new Array(
        this.categorySwipers.length
      ).fill(0);
    });
  }

  get filteredBuffetCategories(): BuffetCategory[] {
    let buffetCats: BuffetCategory[] = [];
    Object.entries(this.buffetCategories).forEach(([index, value]) => {
      if (index !== BuffetCategories.Home && value.epgList)
        buffetCats.push(value);
    });
    return buffetCats;
  }

  get activeSwiperInstance(): Swiper {
    return this.categorySwipers[this.activeSwiperIndex];
  }

  async handleSwiperActions(event: KeyboardEvent): Promise<void> {
    if (!this.getCardControlsStatus) {
      let activeSlide = this.swiperActiveSlideIndexes[this.activeSwiperIndex]; //index of active/focused slide of active swiper
      let swiperEpgList: BuffetEpgItem[] = []; //array of items in active swiper
      if (
        typeof this.filteredBuffetCategories[this.activeSwiperIndex].epgList !==
        "undefined"
      ) {
        swiperEpgList = this.filteredBuffetCategories[this.activeSwiperIndex]
          .epgList as BuffetEpgItem[];
      }
      switch (keyCodeFinder(event.keyCode)) {
        case KeySet.KEY_LEFT:
          if (activeSlide === 0) {
            SpatialNavigation.resume();
            SpatialNavigation.focus("#" + this.$route.name);
            return;
          }

          if (
            (activeSlide >= 2 && activeSlide <= swiperEpgList.length - 3) ||
            (this.smallSwiper &&
              activeSlide >= 1 &&
              activeSlide <= swiperEpgList.length - 2)
          ) {
            this.activeSwiperInstance?.slidePrev();
          }

          if (activeSlide > 0)
            this.swiperActiveSlideIndexes[this.activeSwiperIndex]--;
          activeSlide--;
          SpatialNavigation.resume();
          break;
        case KeySet.KEY_RIGHT:
          if (
            (activeSlide >= 2 && activeSlide <= swiperEpgList.length - 3) ||
            (this.smallSwiper &&
              activeSlide >= 1 &&
              activeSlide <= swiperEpgList.length - 2)
          ) {
            this.activeSwiperInstance?.slideNext();
          }

          if (activeSlide < swiperEpgList.length)
            this.swiperActiveSlideIndexes[this.activeSwiperIndex]++;
          activeSlide++;
          SpatialNavigation.resume();
          break;
        case KeySet.KEY_DOWN:
          if (this.activeSwiperIndex < this.categorySwipers.length - 1) {
            this.activeSwiperIndex++;
          }
          activeSlide = this.swiperActiveSlideIndexes[this.activeSwiperIndex];
          if (
            typeof this.filteredBuffetCategories[this.activeSwiperIndex]
              .epgList !== "undefined"
          ) {
            swiperEpgList = this.filteredBuffetCategories[
              this.activeSwiperIndex
            ].epgList as BuffetEpgItem[];
          }
          SpatialNavigation.resume();
          setTimeout(() => {
            SpatialNavigation.focus("#epgItem" + swiperEpgList[activeSlide].id);
          });
          break;
        case KeySet.KEY_UP:
          if (this.activeSwiperIndex > 0) {
            this.activeSwiperIndex--;
            activeSlide = this.swiperActiveSlideIndexes[this.activeSwiperIndex];
            if (
              typeof this.filteredBuffetCategories[this.activeSwiperIndex]
                .epgList !== "undefined"
            ) {
              swiperEpgList = this.filteredBuffetCategories[
                this.activeSwiperIndex
              ].epgList as BuffetEpgItem[];
            }
            SpatialNavigation.resume();
            setTimeout(() => {
              SpatialNavigation.focus(
                "#epgItem" + swiperEpgList[activeSlide].id
              );
            });

            return;
          }

          if (this.filteredBuffetCategories[0].epgList) {
            SpatialNavigation.focus("#idCat0");
          }
          break;
      }
    }
  }

  //@TODO: refactor refocus logic to be more centralized
  focusSlideFromFilter(
    event: CustomEvent<SpatialNavigationEvent>,
    swiperIndex: number
  ): void {
    if (
      event.detail.direction === "down" &&
      event.detail.previousElement.id === "idCat0"
    ) {
      let swiperEpgList: BuffetEpgItem[] = [];
      if (
        typeof this.filteredBuffetCategories[swiperIndex].epgList !==
        "undefined"
      ) {
        swiperEpgList = this.filteredBuffetCategories[swiperIndex]
          .epgList as BuffetEpgItem[];
      }

      setTimeout(() => {
        SpatialNavigation.focus(
          "#epgItem" +
            swiperEpgList[this.swiperActiveSlideIndexes[swiperIndex]].id
        );
      });
    }
  }

  focusPlayedChannel(): void {
    if (this.getPlayedEpgItemPos) {
      let swiperEpgList: BuffetEpgItem[] = [];
      if (
        typeof this.filteredBuffetCategories[
          this.getPlayedEpgItemPos.swiperIndex
        ].epgList !== "undefined"
      ) {
        swiperEpgList = this.filteredBuffetCategories[
          this.getPlayedEpgItemPos.swiperIndex
        ].epgList as BuffetEpgItem[];
      }

      this.categorySwipers[this.getPlayedEpgItemPos.swiperIndex].once(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        "slideChangeTransitionEnd",
        () => {
          const epgItemPosLocal = this.getPlayedEpgItemPos
            ? this.getPlayedEpgItemPos.slideIndex
            : 0;
          if (
            (epgItemPosLocal > 2 &&
              epgItemPosLocal < swiperEpgList.length - 3) ||
            (this.smallSwiper &&
              epgItemPosLocal >= 1 &&
              epgItemPosLocal <= swiperEpgList.length - 2)
          ) {
            SpatialNavigation.focus(
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              "#epgItem" + swiperEpgList[this.getPlayedEpgItemPos.slideIndex].id
            );

            //setting values for active swiper and epg item
            this.activeSwiperIndex = this.getPlayedEpgItemPos
              ? this.getPlayedEpgItemPos.swiperIndex
              : 0;
            this.swiperActiveSlideIndexes[this.activeSwiperIndex] = this
              .getPlayedEpgItemPos
              ? this.getPlayedEpgItemPos.slideIndex
              : 0;

            //resetting focused item coordinates to prevent unwanted refocusing
            this.setPlayedEpgItemPos();
            this.setPlayedEpgItemId();
          }
        }
      );

      if (
        this.getPlayedEpgItemPos.slideIndex > 2 &&
        this.getPlayedEpgItemPos.slideIndex < swiperEpgList.length - 3
      ) {
        this.categorySwipers[this.getPlayedEpgItemPos.swiperIndex]?.slideTo(
          this.getPlayedEpgItemPos.slideIndex - 2
        );
      } else if (
        this.smallSwiper &&
        this.getPlayedEpgItemPos.slideIndex >= 1 &&
        this.getPlayedEpgItemPos.slideIndex <= swiperEpgList.length - 2
      ) {
        this.categorySwipers[this.getPlayedEpgItemPos.swiperIndex]?.slideTo(
          this.getPlayedEpgItemPos.slideIndex - 1
        );
      } else {
        SpatialNavigation.focus(
          "#epgItem" + swiperEpgList[this.getPlayedEpgItemPos.slideIndex].id
        );
        //setting values for active swiper and epg item
        this.activeSwiperIndex = this.getPlayedEpgItemPos.swiperIndex;
        this.swiperActiveSlideIndexes[this.activeSwiperIndex] =
          this.getPlayedEpgItemPos.slideIndex;

        //resetting focused item coordinates to prevent unwanted refocusing
        this.setPlayedEpgItemPos();
        this.setPlayedEpgItemId();
      }

      SpatialNavigation.resume();
    }
  }

  checkWindowWidth(): void {
    this.smallSwiper = window.innerWidth <= 992;
  }

  @Watch("getUpdatedBuffetItems")
  handleBuffetUpdate(newVal: boolean): void {
    if (!newVal) {
      this.$nextTick(() => {
        for (let i = 0; i < this.categorySwipers.length; i++) {
          this.categorySwipers[i].update();
        }
        this.focusPlayedChannel();
      });
    }
  }

  beforeDestroy(): void {
    window.removeEventListener("resize", this.checkWindowWidth);
  }
}
